import { Star, MapPin, Briefcase, Calendar } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import type { Candidate } from '@/data/mockData';

interface CandidateCardProps {
  candidate: Candidate;
  onViewProfile: (candidate: Candidate) => void;
  onShortlist: (candidate: Candidate) => void;
  onScheduleInterview: (candidate: Candidate) => void;
}

const statusStyles = {
  new: 'bg-blue-100 text-blue-700',
  reviewed: 'bg-yellow-100 text-yellow-700',
  shortlisted: 'bg-primary/20 text-primary-foreground',
  interviewed: 'bg-accent/20 text-accent-foreground',
  hired: 'bg-success/20 text-success',
  rejected: 'bg-destructive/20 text-destructive',
};

export const CandidateCard = ({
  candidate,
  onViewProfile,
  onShortlist,
  onScheduleInterview,
}: CandidateCardProps) => {
  const initials = candidate.name
    .split(' ')
    .map((n) => n[0])
    .join('')
    .toUpperCase();

  return (
    <div className="group rounded-xl border border-border bg-card p-5 transition-all hover:border-primary/30 hover:shadow-lg">
      <div className="flex gap-4">
        {/* Avatar */}
        <div className="relative">
          <div className="flex h-16 w-16 items-center justify-center rounded-full bg-gradient-to-br from-primary to-accent text-xl font-bold text-white">
            {initials}
          </div>
          {candidate.isOnline && (
            <span className="absolute bottom-0 right-0 h-4 w-4 rounded-full border-2 border-card bg-success" />
          )}
        </div>

        {/* Info */}
        <div className="flex-1">
          <div className="flex items-start justify-between">
            <div>
              <h3 className="font-semibold text-foreground">{candidate.name}</h3>
              <p className="text-sm text-muted-foreground">{candidate.title}</p>
            </div>
            <Badge className={cn('capitalize', statusStyles[candidate.status])}>
              {candidate.status}
            </Badge>
          </div>

          {/* Meta Info */}
          <div className="mt-3 flex flex-wrap items-center gap-4 text-sm text-muted-foreground">
            <span className="flex items-center gap-1">
              <MapPin className="h-4 w-4" />
              {candidate.location}
            </span>
            <span className="flex items-center gap-1">
              <Briefcase className="h-4 w-4" />
              {candidate.experience} years
            </span>
            <span className="flex items-center gap-1">
              <Calendar className="h-4 w-4" />
              Applied {new Date(candidate.appliedDate).toLocaleDateString()}
            </span>
            <span className="flex items-center gap-1">
              <Star className="h-4 w-4 fill-primary text-primary" />
              {candidate.rating.toFixed(1)}
            </span>
          </div>

          {/* Skills */}
          <div className="mt-3 flex flex-wrap gap-2">
            {candidate.skills.slice(0, 4).map((skill) => (
              <Badge key={skill} variant="secondary" className="text-xs">
                {skill}
              </Badge>
            ))}
            {candidate.skills.length > 4 && (
              <Badge variant="outline" className="text-xs">
                +{candidate.skills.length - 4} more
              </Badge>
            )}
          </div>
        </div>
      </div>

      {/* Actions */}
      <div className="mt-4 flex gap-2 border-t border-border pt-4">
        <Button variant="outline" size="sm" className="flex-1" onClick={() => onViewProfile(candidate)}>
          View Profile
        </Button>
        {candidate.status !== 'shortlisted' && candidate.status !== 'hired' && (
          <Button variant="default" size="sm" className="flex-1" onClick={() => onShortlist(candidate)}>
            Shortlist
          </Button>
        )}
        <Button variant="secondary" size="sm" className="flex-1" onClick={() => onScheduleInterview(candidate)}>
          Schedule Interview
        </Button>
      </div>
    </div>
  );
};
